import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { LICENSE_CONCLUDED } from '../../../../../shared/featureFlags/flagNames';
import { plural } from '../../../../../shared/helpers';
import { Category, type LicensingIssueType } from '../../../../../shared/issuesV2';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { containEvent } from '../../../../utils';
import Button, { BUTTON_TYPE } from '../../../generic/Button';
import FallbackImage from '../../../generic/FallbackImage';
import Flex from '../../../generic/Flex';
import Icon, { icons } from '../../../generic/Icon';
import List from '../../../generic/List';
import ListRowContainer from '../../../generic/List/ListRowContainer';
import {
  OriginOrganizationNameBadge,
} from '../../../projects/dashboardV2/ProjectsTable/ProjectItem/OriginOrganizationNameBadge';
import { useIssueDetailsModals } from '../IssueDetailsModalsContext';

import JiraTicketLink from './JiraTicketLink';
import { LicenseIssueDetailContent } from './LicenseIssueDetailContent';
import ManualTicketLink from './ManualTicketLink';
import { getIssueDetailURLForScope } from './utils';

import sharedStyles from '../IssueDetails.module.less';
import styles from './IssueDetailsProjectsTable.module.less';

/**
 * IssueDetailsProjectsTable displays a list of affected projects for this issue.
 * When viewing a single project issue, this will just display the project you
 * are looking at. For global and release group issues, it will display all
 * other projects which have this issue in your org.
 */
const IssueDetailsProjectsTable: VoidFunctionComponent = () => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const affectedProjects = useSelector(IssuesV2Store.selectors.getAffectedProjects);
  const asyncStatus = useSelector(IssuesV2Store.selectors.getAffectedProjectsAsyncStatus);
  const issueProjectCount = affectedProjects.length;
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const issueDetailsURL = useSelector(IssuesV2Store.selectors.getIssueDetailsURLFromActiveIssueDetails);

  const { viewPathModal, isAnyModalOpen } = useIssueDetailsModals();

  const isLicenseConcludedEnabled = useIsFeatureEnabled(LICENSE_CONCLUDED);

  if (asyncStatus.errors && asyncStatus.errors.length > 0) {
    return (
      <FallbackImage src="error-plug" alt="Network error" hasPadding>
        <div>Oops! There was an error.</div>
        <div>Please try again in a bit.</div>
      </FallbackImage>
    );
  }

  if (!issueDetails?.id || (!asyncStatus.isLoading && affectedProjects.length === 0)) {
    const errorText = affectedProjects.length === 0 ? 'No projects found.' : 'No issue selected.';

    return (
      <FallbackImage src="no-results" alt={errorText} hasPadding>
        {errorText}
      </FallbackImage>
    );
  }

  return (
    <>
      <div className={styles.projectTableNameAndDescription}>
        <div className={styles.projectTableName}>Affected projects</div>
        <div className={styles.projectTableDescription}>This issue is present in the projects listed below.</div>
      </div>
      <List>
        <List.Header>
          <List.HeaderText>
            {issueProjectCount} {plural(issueProjectCount, 'project')}
          </List.HeaderText>
        </List.Header>
        <List.Body>
          <div className={styles.projectTableBody}>
            {affectedProjects.map((affectedProject) => (
              <ListRowContainer>
                <List.Row
                  key={affectedProject.id}
                  to={getIssueDetailURLForScope({
                    affectedProject,
                    scope,
                    issueDetailsURL,
                    issueCategory,
                    issueDetails,
                  })}
                  openInNewTab
                  left={
                    <div className={styles.projectNameAndStatus}>
                      <div className={styles.projectName}>
                        <span className={styles.projectNameIcon}>
                          <Icon icon={icons.regularFolder} />
                        </span>
                        <span className={styles.projectNameText}>
                          {affectedProject.title}{' '}
                          <OriginOrganizationNameBadge
                            originOrganizationName={affectedProject.originOrganizationName}
                          />
                        </span>
                      </div>
                      <Flex align="center" justify="space-between">
                        {isLicenseConcludedEnabled && issueCategory === Category.Licensing ? (
                          <LicenseIssueDetailContent
                            status={affectedProject.status}
                            issueType={issueDetails.type as LicensingIssueType}
                            // @ts-expect-error - issueDetails guaranteed to be a LicensingIssue with a license field.
                            issueLicense={issueDetails.license}
                            concludedLicenses={affectedProject.concludedLicenses}
                            scope={scope}
                        />
                        ) : (
                          <div className={styles.statusAndTicketRow}>
                            {affectedProject.status === 'ignored' && (
                            <div className={sharedStyles.projectStatus}>
                              <span className={`${sharedStyles.ignoredIcon} ${sharedStyles.icon}`}>
                                <Icon icon={icons.eyeSlashRegular} />
                              </span>
                              Ignored
                            </div>
                            )}
                            {affectedProject.status === 'active' && (
                            <div className={sharedStyles.projectStatus}>
                              <span className={`${sharedStyles.activeIcon} ${sharedStyles.icon}`}>
                                <Icon icon={icons.exclamationCircleRegular} />
                              </span>
                              Active
                            </div>
                            )}
                          </div>
                        )}
                        <div className={styles.statusAndTicketRow}>
                          {affectedProject.type === 'jira' && (
                          <JiraTicketLink
                            jiraTicketId={affectedProject.jiraTicketId}
                            jiraTicketURL={affectedProject.jiraTicketURL}
                            />
                          )}
                          {affectedProject.type === 'custom' && (
                          <ManualTicketLink url={affectedProject.customTicketUrl} />
                          )}
                        </div>
                      </Flex>
                    </div>
                  }
                  right={
                    <Button
                      type={BUTTON_TYPE.SECONDARY}
                      isDisabled={isAnyModalOpen}
                      onClick={containEvent(() => {
                        //  Note: we containEvent here becuase the project table row is a link to that actual project.
                        viewPathModal.onOpen(affectedProject);
                      })}
                    >
                      View path
                    </Button>
                  }
                />
              </ListRowContainer>
            ))}
          </div>
        </List.Body>
      </List>
    </>
  );
};

export default IssueDetailsProjectsTable;
